import React, { useContext } from 'react';

import { RouteParams } from '@search/graphql-typings';
import { useGql, graphql } from '@search/gql-client/src/useGql';

import { Shared } from '../../../../types/shared';

import { OnChangeGlobalContext, IOnChangeGlobalContext, GlobalContext, IGlobalContext } from '../GlobalContext';
import { useSEOTexts } from '../SeoTexts/SEOTextsProvider';
import { useBreadcrumbs } from '../NewBuildingCardPage/useBreadcrumbs';
import { Meta } from '../Meta';

import type {
    NewBuildingConstructionPageSeoQuery$variables as NewBuildingConstructionPageSeoQueryVariables, NewBuildingConstructionPageSeoQuery$data as NewBuildingConstructionPageSeoQueryResponse
} from './__generated__/NewBuildingConstructionPageSeoQuery.graphql';

export const cardQuery = graphql`
    query NewBuildingConstructionPageSeoQuery(
        $id: Int!
        $whiteLabel: NewBuildingWhitelabelEnum
    ) {
        newBuilding(
            id: $id
            whiteLabel: $whiteLabel
        ) {
            narrowRegionId(whiteLabel: $whiteLabel)
            narrowRegionLocative(whiteLabel: $whiteLabel)
            title {
                nominative
            }
            gallery {
                main {
                    large
                }
            }
            seo(whiteLabel: $whiteLabel) {
                title,
                metaDescription
            }
            constructionSeo(whiteLabel: $whiteLabel) {
                title,
                metaDescription
            }
            routeParams {
                id
                name
                type
                regionId
                region
                regionId
                subRegion
            }
        }
    }
`;

const NewBuildingConstructionPageSeo = (
    props: Shared.NewBuildingConstructionPageProps
) => {
    const onChangeGlobalContext = useContext<IOnChangeGlobalContext>(OnChangeGlobalContext);
    const globalContext = useContext<IGlobalContext>(GlobalContext);
    const { instanceConfig: { searchIndex } } = globalContext;
    const id = Number(props.params.id);
    const { data } = useGql<NewBuildingConstructionPageSeoQueryVariables, NewBuildingConstructionPageSeoQueryResponse>(
        cardQuery, {
            id,
            whiteLabel: searchIndex
        }
    );

    const params = Object.assign({}, data!.newBuilding?.routeParams);

    const breadcrumbs = useBreadcrumbs({
        narrowRegionId: data!.newBuilding!.narrowRegionId,
        narrowRegionLocative: data!.newBuilding!.narrowRegionLocative,
        routeParams: data!.newBuilding!.routeParams as RouteParams,
        title: data!.newBuilding!.title.nominative,
        isConstruction: true
    });

    const breadcrumbsMeta = breadcrumbs.rdfMeta;

    const canonicalUrl = onChangeGlobalContext.linkBuilder.construction({
        id: params.id,
        // @ts-ignore
        type: params.type,
        name: params.name,
        region: params.region,
        subRegion: params.subRegion
    }).split('?')[0];

    const {
        constructionSeo: { title, metaDescription }
    } = data!.newBuilding!;

    let image = data?.newBuilding?.gallery?.main?.large;

    if (image && image.startsWith('//')) {
        image = `https:${image}`;
    }

    const { seoTexts } = useSEOTexts(canonicalUrl);

    return (
        <>
            <Meta
                title={seoTexts?.title ?? title}
                description={seoTexts?.description ?? metaDescription}
                canonical={canonicalUrl}
                image={image}
            />
            {breadcrumbsMeta ? (
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: breadcrumbsMeta }}
                />
            ) : null}
        </>
    );
};

NewBuildingConstructionPageSeo.displayName = 'NewBuildingConstructionPageSeo';

export default NewBuildingConstructionPageSeo;
